import React from 'react';
import classNames from 'classnames';
import ScorecardAnswerBubbles from 'components/pure/scorecards/ScorecardAnswerBubbles';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import TextAreaField from 'components/pure/utils/form/TextAreaField';
import InputField from 'components/pure/utils/form/InputField';
import BookmarkIcon from 'components/pure/utils/icon/BookmarkIcon';
import CoachingReviewAnswer from 'records/CoachingReviewAnswer';
import {QUESTION_TYPE_TYPE, RESPONDENT_TYPE} from 'records/CoachingReviewTypeQuestion';
import './ReviewTypeQuestions.styl';
import partitionBy from 'utils/partitionBy';
import anyQuestionHasAnAnswer from 'components/coaching/utils/anyQuestionHasAnAnswer';
import GenericLink from 'components/GenericLink';

export default class ReviewTypeQuestions extends React.PureComponent {
  render() {
    const {
      reviewType,
      answers,
      onChange,
      disabled,
      coachOverrideRepQuestions,
      onCoachOverride,
      isPrepping,
      isCoach,
      isRecipient,
      otherUser,
      errors,
      adHoc,
    } = this.props;

    if (!reviewType || reviewType.questions.isEmpty()) {
      return null;
    }

    const [repQuestions, coachQuestions] = partitionBy(
      reviewType.questions,
      q => q.respondent === RESPONDENT_TYPE.REP
    );

    const myQuestions = isRecipient ? repQuestions : coachQuestions;
    const theirQuestions = isRecipient ? coachQuestions : repQuestions;

    return (
      <div className="review-type-questions-wrapper">
        <h5>{reviewType.includesCalls ? 'Additional Review' : 'Questions for Review'}</h5>
        <div>
          <MyQuestionList
            myQuestions={myQuestions}
            theirQuestions={theirQuestions}
            isCoach={isCoach}
            isRecipient={isRecipient}
            onChange={onChange}
            answers={answers}
            disabled={disabled}
            errors={errors}
          />
          <TheirQuestionList
            otherUser={otherUser}
            myQuestions={myQuestions}
            theirQuestions={theirQuestions}
            isCoach={isCoach}
            isRecipient={isRecipient}
            coachOverrideRepQuestions={coachOverrideRepQuestions}
            onCoachOverride={onCoachOverride}
            isPrepping={isPrepping}
            onChange={onChange}
            answers={answers}
            disabled={disabled}
            errors={errors}
            adHoc={adHoc}
          />
        </div>
      </div>
    );
  }
}

const MyQuestionList = ({
  myQuestions,
  theirQuestions,
  isCoach,
  isRecipient,
  onChange,
  answers,
  disabled,
  errors,
}) => {
  if (myQuestions.isEmpty()) {
    return null;
  }
  return (
    <div>
      {theirQuestions.size > 0 && (
        <div className="question-section-header">
          <header>My Questions</header>
        </div>
      )}
      <ReviewQuestionList
        questions={myQuestions}
        isCoach={isCoach}
        isRecipient={isRecipient}
        onChange={onChange}
        answers={answers}
        disabled={disabled}
        errors={errors}
      />
    </div>
  );
};

const TheirQuestionList = ({
  otherUser,
  myQuestions,
  theirQuestions,
  isCoach,
  isRecipient,
  onChange,
  answers,
  disabled,
  errors,
  onCoachOverride,
  coachOverrideRepQuestions,
  isPrepping,
  adHoc,
}) => {
  if (theirQuestions.isEmpty()) {
    return null;
  }
  return (
    <div>
      <TheirQuestionHeader
        otherUser={otherUser}
        myQuestions={myQuestions}
        theirQuestions={theirQuestions}
        isCoach={isCoach}
        answers={answers}
        disabled={disabled}
        onCoachOverride={onCoachOverride}
        coachOverrideRepQuestions={coachOverrideRepQuestions}
        isPrepping={isPrepping}
        adHoc={adHoc}
      />
      <ReviewQuestionList
        questions={theirQuestions}
        isCoach={isCoach}
        isRecipient={isRecipient}
        onChange={onChange}
        answers={answers}
        disabled={disabled}
        coachOverrideRepQuestions={coachOverrideRepQuestions}
        errors={errors}
      />
    </div>
  );
};

const TheirQuestionHeader = ({
  otherUser,
  myQuestions,
  theirQuestions,
  isCoach,
  answers,
  disabled,
  onCoachOverride,
  coachOverrideRepQuestions,
  isPrepping,
  adHoc,
}) => {
  if (myQuestions.isEmpty()) {
    return null;
  }
  const otherUserFirstName = otherUser ? otherUser.firstName : '';
  const showHint = isCoach && !anyQuestionHasAnAnswer(theirQuestions, answers) && !adHoc;

  const showCoachOverride = isCoach && !coachOverrideRepQuestions && !disabled && !isPrepping;

  return (
    <div className="question-section-header">
      <div className="left-header">
        <header>Questions for {otherUserFirstName}</header>
        {showHint && <div>{hintMessage(disabled, otherUserFirstName)}</div>}
      </div>
      {showCoachOverride && (
        <div>
          <GenericLink onClick={onCoachOverride}>Edit {otherUserFirstName}'s Answers</GenericLink>
        </div>
      )}
    </div>
  );
};

const hintMessage = (disabled, otherUserFirstName) => {
  const hintVerb = disabled ? 'has been' : 'was';
  return `(${otherUserFirstName} ${hintVerb} asked to fill these out in advance of the meeting.)`;
};

const ReviewQuestionList = ({
  questions,
  isCoach,
  isRecipient,
  answers,
  errors,
  disabled,
  onChange,
  coachOverrideRepQuestions,
}) => {
  return (
    <section className="review-type-questions">
      {questions
        .sortBy(question => question.index)
        .map(question => {
          const respondentIsOtherUser = !(
            (isCoach && question.respondent === 'COACH') ||
            (isRecipient && question.respondent === 'REP')
          );
          const answer =
            answers.get(question.id) || new CoachingReviewAnswer({reviewQuestionId: question.id});

          const shouldDisable =
            disabled || (respondentIsOtherUser && !(isCoach && coachOverrideRepQuestions));

          return (
            <div
              className={classNames('review-type-question-container', {
                'other-reviewer-question': shouldDisable,
              })}
              key={question.id}>
              {question.coachNotes && (
                <div className="notes-wrapper">
                  <div className="coach-notes">
                    <BookmarkIcon />
                    {question.coachNotes}
                  </div>
                </div>
              )}
              <FieldContainer
                label={question.text}
                error={errors.get(question.id)}
                labelType="div"
                className="review-type-question">
                {question.type === QUESTION_TYPE_TYPE.TextArea ? (
                  <TextAreaField
                    disabled={shouldDisable}
                    value={answer.comment}
                    onChange={text =>
                      onChange(answers.set(question.id, answer.set('comment', text)))
                    }
                    rows={3}
                  />
                ) : question.type === QUESTION_TYPE_TYPE.TextField ? (
                  <InputField
                    disabled={shouldDisable}
                    value={answer.comment || ''}
                    onChange={text =>
                      onChange(answers.set(question.id, answer.set('comment', text)))
                    }
                  />
                ) : (
                  <ScorecardAnswerBubbles
                    selectKey="value"
                    disabled={shouldDisable}
                    scoringScheme={question.type}
                    question={question}
                    answer={answer.score}
                    onSelectAnswer={(_, score) =>
                      onChange(answers.set(question.id, answer.set('score', score)))
                    }
                  />
                )}
              </FieldContainer>
            </div>
          );
        })}
    </section>
  );
};
